/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { IMediaModel, MediaCategoryType, getMediaTagsByType } from "@nf/common";
import { joinWithFinalSeparator } from "@nf/helpers";
import { useTranslation } from "next-i18next";

import styles from "./MediaSpecificationList.module.scss";

interface MediaSpecificationListProps {
  media: IMediaModel;
}

const Row = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className={styles.row}>
    <h3 className={styles.label}>{label}</h3>
    <p className={styles.value}>{children}</p>
  </div>
);

export const MediaSpecificationList = ({
  media,
}: MediaSpecificationListProps) => {
  const { t } = useTranslation("translations");

  const spokenLanguages =
    media.Languages?.map((language) => language.Name) ?? [];
  const subtitles = media.Subtitles ?? [];
  const minimumAge = media.MediaAgeRestrictionValueMin;
  const formats = getMediaTagsByType(
    media.Categories,
    MediaCategoryType.Format
  ).map((format) => format.CategoryName);

  return (
    <p className={styles.specifications}>
      {spokenLanguages.length > 0 && (
        <Row label={t("web_asset_page_spoken_language")}>
          {joinWithFinalSeparator(spokenLanguages)}
        </Row>
      )}

      {subtitles.length > 0 && (
        <Row label={t("web_asset_page_subtitles")}>
          {joinWithFinalSeparator(subtitles)}
        </Row>
      )}

      <Row label={t("web_asset_page_orginal_title")}>{media.Title}</Row>

      {media.ContentAdvisory && (
        <Row label={t("web_asset_page_content_advisory")}>
          {media.ContentAdvisory}
        </Row>
      )}

      {formats?.length > 0 && (
        <Row label={t("web_asset_page_format")}>
          {joinWithFinalSeparator(formats)}
        </Row>
      )}

      <Row label={t("web_asset_page_age_limit")}>
        {minimumAge && parseInt(minimumAge) > 0
          ? `${minimumAge}${t("web_asset_page_agelimit_year")}`
          : t("web_asset_page_agelimit_allowed_all")}
      </Row>
    </p>
  );
};

export default MediaSpecificationList;
